import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  WHITELISTED_EXTENSIONS,
  MAX_NUMBER_OF_FILES,
} from '@gorgias-help-center/shared/contact-form/feature-upload-attachments/google-storage-attachment.constants';
import { SignedPostPolicyDto } from '@gorgias-help-center/shared/contact-form/feature-upload-attachments/signed-post-policy-response.dto';
import { CloudStorageService } from '../../../lib/cloud-storage/api';
import { UploadFile, AttachedFileProps } from './upload.types';

export {
  WHITELISTED_EXTENSIONS,
  MAX_FILE_SIZE_BYTES,
  MAX_NUMBER_OF_FILES,
  MAX_FILE_SIZE_MB,
};

/**
 * Firefox does not handle heic/heif MIME type when getting file from
 * drag and drop event, we need to provide it ourselves for the user to
 * be able to send his attachments.
 */
export const getFileTypeForFirefox = (fileName: string): string => {
  const fileExt = fileName.split('.').pop();

  switch (fileExt?.toLowerCase()) {
    case 'heic':
      return 'image/heic';
    case 'heif':
      return 'image/heif';
    default:
      return '';
  }
};

/**
 * Copied from the chat
 */
export const uploadGoogleAttachment: UploadFile = async (
  options: {
    file: File;
    signedPostPolicy: SignedPostPolicyDto;
  },
  id: string,
  updateFileStatus,
) => {
  const { file, signedPostPolicy } = options;
  const { url, fields } = signedPostPolicy;

  const fileType = file.type ?? getFileTypeForFirefox(file.name);
  let percentage = 0;

  const attachmentUrl = encodeURI(url + fields.key);

  const formdata = new FormData();
  for (const prop in fields) {
    formdata.append(prop, fields[prop]);
  }
  formdata.append('Content-Type', fileType);
  formdata.append('file', file, file.name);

  function handleUploadProgress({ loaded, total }: ProgressEvent) {
    percentage = Math.round((loaded / total) * 100);

    updateFileStatus(percentage, 'uploading', id);
  }

  function handleLoadEnd() {
    updateFileStatus(100, 'uploaded', id);
  }

  function handleLoadStart() {
    updateFileStatus(percentage, 'uploading', id);
  }

  function handleError() {
    updateFileStatus(0, 'error', id);
  }

  function addListeners(xhr: XMLHttpRequest) {
    xhr.onloadstart = handleLoadStart;
    xhr.onloadend = handleLoadEnd;
    xhr.upload.onprogress = handleUploadProgress;
    xhr.onerror = handleError;
    xhr.onabort = handleError;
  }
  try {
    // Post the file to the Google Cloud Storage using XMLHttpRequest
    const xhr = new XMLHttpRequest();

    xhr.open('POST', url, true);

    addListeners(xhr);

    xhr.send(formdata);
  } catch {
    throw new Error('Failed to upload file');
  }

  return attachmentUrl;
};

export const getSignedPostPolicy = async (file: File) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`File should be smaller than ${MAX_FILE_SIZE_MB} MB`);
  }

  let fileType = file.type;
  if (file.type === '' && file.name) {
    fileType = getFileTypeForFirefox(file.name);
  }

  const policy = await CloudStorageService.getCloudStoragePolicy({
    filename: file.name,
    size: file.size,
    mimetype: fileType,
  });

  return policy.data;
};

export const prepareAttachments = (attachments: AttachedFileProps[]) =>
  attachments.map((attachment) => ({
    filename: attachment.file.name,
    size: attachment.file.size,
    url: attachment.url ?? '',
  }));
